import { Injectable, NgZone } from '@angular/core';
import { getNonce } from 'core/utils/csp-nonce';
import { BehaviorSubject, Observable } from 'rxjs';

/*
 * Common service shared by all reCaptcha component instances
 * through dependency injection.
 * This service has the task of loading the reCaptcha API once for all.
 * Only the first instance of the component creates the service, subsequent
 * components will use the existing instance.
 *
 * As the language is passed to the <script>, the first component
 * determines the language of all subsequent components. This is a limitation
 * of the present Google API.
 */
@Injectable({ providedIn: 'root' })
export class ReCaptchaService {
  private scriptLoaded = false;
  private readySubject = new BehaviorSubject(false);

  constructor(zone: NgZone) {
    /* the callback needs to exist before the API is loaded */
    if (typeof window !== 'undefined') {
      window[<any>'reCaptchaOnloadCallback'] = <any>(() => zone.runOutsideAngular(this.onloadCallback.bind(this)));
    }
  }
  /**
   * Returns an observable that emits true when the reCaptcha script is loaded.
   * The script is loaded only once, subsequent calls will return the same observable.
   * @param language - The language to be used for the reCaptcha.
   * @param _global - Not used, kept for compatibility with other implementations.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public getReady(language: string, _global: boolean): Observable<boolean> {
    if (!this.scriptLoaded) {
      const scriptUrl = 'www.recaptcha.net';
      this.scriptLoaded = true;
      const doc = <HTMLDivElement>document.body;
      const script = document.createElement('script');
      script.nonce = getNonce();
      script.innerHTML = '';
      script.src =
        `https://${scriptUrl}/recaptcha/api.js?onload=reCaptchaOnloadCallback&render=explicit` +
        (language ? '&hl=' + language : '');
      script.async = true;
      script.defer = true;
      doc.appendChild(script);
    }
    return this.readySubject.asObservable();
  }

  private onloadCallback() {
    this.readySubject.next(true);
  }
}
